import React, { useContext, useRef, useState } from "react";
import { graphql, navigate } from "gatsby";
import { ArboretumSearchResults } from "./ArboretumSearchResults";
import { KontentComponentSettings, StringValue } from "../../../types";
import { getMultiChoice } from "../../../lib/multichoice";
import { PageMetadataContext } from "../../../lib/PageMetadataContext";

interface Props extends KontentComponentSettings {
  collection_tooltip: StringValue;
  average_tree_size_tooltip: StringValue;
  family_tooltip: StringValue;
  year_planted_tooltip: StringValue;
  growth_rate_tooltip: StringValue;
  sun_requirements_tooltip: StringValue;
  water_requirements_tooltip: StringValue;
  soil_requirements_tooltip: StringValue;
  native_range_tooltip: StringValue;
  hardiness_zone_tooltip: StringValue;
  season_of_interest_tooltip: StringValue;
  supported_wildlife_tooltip: StringValue;
}

const ArboretumSearchResultsContainer: React.FC<Props> = ({
  id,
  component_settings__anchor_name,
  component_settings__hide_on_mobile,
  collection_tooltip,
  average_tree_size_tooltip,
  family_tooltip,
  year_planted_tooltip,
  growth_rate_tooltip,
  sun_requirements_tooltip,
  water_requirements_tooltip,
  soil_requirements_tooltip,
  native_range_tooltip,
  hardiness_zone_tooltip,
  season_of_interest_tooltip,
  supported_wildlife_tooltip,
  ...props
}) => {
  const location = useContext(PageMetadataContext).location;

  const searchTerm = location?.state?.data?.searchText;

  const queryParams = new URLSearchParams(location?.search);

  const searchParam = queryParams.get("q");

  const refinementList = {};

  for (const [key, value] of queryParams.entries()) {
    if (key !== "q") {
      refinementList[key] = value.split(",");
    }
  }

  const createURL = (searchState: any) => {
    let newParams = [];

    if (searchState.query) {
      newParams.push(`q=${encodeURIComponent(searchState.query)}`);
    }

    if (searchState?.refinementList) {
      const refinedFilters = Object.keys(searchState?.refinementList);

      refinedFilters.forEach((filter) =>
        searchState.refinementList[filter]
          ? newParams.push(
              `${filter}=${searchState.refinementList[filter]
                ?.map((refinement: string) => encodeURIComponent(refinement))
                .join(",")}`
            )
          : ""
      );
    }

    return newParams.length ? `#search?${newParams.join("&")}` : "";
  };

  const searchStateToUrl = (searchState: any) =>
    searchState?.query ? createURL(searchState) : "#search";

  const [query, setQuery] = useState({
    query: searchParam || searchTerm,
    refinementList: refinementList,
  });

  const DEBOUNCE_URL_SYNC = 400;
  const debouncedSetStateRef = useRef<any>(null);

  const onSearchTermChange = (updatedSearchState: any) => {
    clearTimeout(debouncedSetStateRef.current);

    debouncedSetStateRef.current = setTimeout(
      () => navigate(searchStateToUrl(updatedSearchState), { replace: true }),
      DEBOUNCE_URL_SYNC
    );

    setQuery(updatedSearchState);
  };

  return (
    <ArboretumSearchResults
      id={id}
      anchor={component_settings__anchor_name?.value}
      hideOnMobile={
        getMultiChoice(component_settings__hide_on_mobile) === "yes"
      }
      collectionTooltip={collection_tooltip?.value}
      averageTreeSizeTooltip={average_tree_size_tooltip?.value}
      familyTooltip={family_tooltip?.value}
      yearPlantedTooltip={year_planted_tooltip?.value}
      growthRateTooltip={growth_rate_tooltip?.value}
      sunRequirementsTooltip={sun_requirements_tooltip?.value}
      waterRequirementsTooltip={water_requirements_tooltip?.value}
      soilRequirementsTooltip={soil_requirements_tooltip?.value}
      nativeRangeTooltip={native_range_tooltip?.value}
      hardinessZoneTooltip={hardiness_zone_tooltip?.value}
      seasonOfInterestTooltip={season_of_interest_tooltip?.value}
      supportedWildlifeTooltip={supported_wildlife_tooltip?.value}
      searchTerm={query}
      onSearchTermChange={onSearchTermChange}
    ></ArboretumSearchResults>
  );
};

export default ArboretumSearchResultsContainer;

export const fragmentArboretumSearchResultsContainer = graphql`
  fragment ArboretumSearchResults on kontent_item_component___arboretum_search_results {
    elements {
      collection_tooltip {
        value
      }
      average_tree_size_tooltip {
        value
      }
      family_tooltip {
        value
      }
      year_planted_tooltip {
        value
      }
      growth_rate_tooltip {
        value
      }
      sun_requirements_tooltip {
        value
      }
      water_requirements_tooltip {
        value
      }
      soil_requirements_tooltip {
        value
      }
      native_range_tooltip {
        value
      }
      hardiness_zone_tooltip {
        value
      }
      season_of_interest_tooltip {
        value
      }
      supported_wildlife_tooltip {
        value
      }
      component_settings__anchor_name {
        value
      }
      component_settings__hide_on_mobile {
        value {
          codename
        }
      }
    }
    id
  }
`;
